import {
    Component,
    Input,
    OnDestroy,
    ElementRef,
    ViewChild,
    AfterViewInit,
    OnChanges,
    SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import * as pbi from 'powerbi-client';
import {
    PowerBIService,
    EmbedConfig,
} from '../../../../services/powerbi.service';

/** Error de carga con código de soporte opcional (ej. OPB-PBI-001) */
interface EmbedError extends Error {
    errorCode?: string;
}

/**
 * Componente reutilizable para embeber reportes de Power BI
 *
 * Uso:
 * <app-powerbi-report
 *   [reportId]="'08e42ebb-d9c5-4722-9839-fc934070ff40'"
 *   [username]="'A'"
 *   [roles]="['DEMOROLE']"
 *   [height]="'600px'">
 * </app-powerbi-report>
 */
@Component({
    selector: 'app-powerbi-report',
    standalone: true,
    imports: [
        CommonModule,
        MatProgressSpinnerModule,
        MatCardModule,
        MatIconModule,
        MatButtonModule,
    ],
    template: `
        <div class="powerbi-container" [style.height]="height">
            <!-- Loading state -->
            <div *ngIf="isLoading" class="loading-overlay">
                <mat-spinner diameter="50"></mat-spinner>
                <p>Cargando reporte...</p>
            </div>

            <!-- Error state -->
            <div *ngIf="error && !isLoading" class="error-container">
                <mat-icon class="error-icon">error_outline</mat-icon>
                <h3>No se pudo cargar el reporte</h3>
                <p>{{ error }}</p>
                <p *ngIf="errorCode" class="error-code">
                    Código de error: <strong>{{ errorCode }}</strong>
                </p>
                <button
                    mat-raised-button
                    color="primary"
                    (click)="loadReport()"
                >
                    <mat-icon>refresh</mat-icon>
                    Reintentar
                </button>
            </div>

            <!-- Power BI Report container -->
            <div
                #reportContainer
                class="report-container"
                [style.display]="isLoading || error ? 'none' : 'block'"
            ></div>
        </div>
    `,
    styles: [
        `
            .powerbi-container {
                position: relative;
                width: 100%;
                min-height: 400px;
                background-color: #f5f5f5;
                border-radius: 8px;
                overflow: hidden;
            }

            .loading-overlay {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                background-color: rgba(255, 255, 255, 0.9);
                z-index: 10;
            }

            .loading-overlay p {
                margin-top: 16px;
                color: #666;
            }

            .error-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 100%;
                padding: 24px;
                text-align: center;
            }

            .error-icon {
                font-size: 64px;
                width: 64px;
                height: 64px;
                color: #f44336;
                margin-bottom: 16px;
            }

            .error-container h3 {
                margin: 0 0 8px 0;
                color: #333;
            }

            .error-container p {
                color: #666;
                margin-bottom: 16px;
            }

            .error-code {
                font-size: 13px;
                color: #999 !important;
            }

            .error-code strong {
                font-family: 'Roboto Mono', 'Courier New', monospace;
                color: #666;
                letter-spacing: 0.5px;
            }

            .report-container {
                width: 100%;
                height: 100%;
            }

            :host ::ng-deep iframe {
                border: none;
            }
        `,
    ],
})
export class PowerBIReportComponent
    implements OnDestroy, AfterViewInit, OnChanges
{
    @ViewChild('reportContainer') reportContainer!: ElementRef<HTMLDivElement>;

    /** ID del reporte a mostrar */
    @Input() reportId!: string;

    /** Username para Row Level Security (opcional) */
    @Input() username?: string;

    /** Roles para Row Level Security (opcional) */
    @Input() roles?: string[];

    /** Altura del contenedor (default: 600px) */
    @Input() height = '600px';

    isLoading = true;
    error: string | null = null;
    errorCode: string | null = null;

    private powerbiService: pbi.service.Service;
    private report: pbi.Report | null = null;
    private embedConfig: EmbedConfig | null = null;

    constructor(private powerBIApiService: PowerBIService) {
        this.powerbiService = new pbi.service.Service(
            pbi.factories.hpmFactory,
            pbi.factories.wpmpFactory,
            pbi.factories.routerFactory
        );
    }

    ngAfterViewInit(): void {
        if (this.reportId) {
            this.loadReport();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Si cambia el reportId o username, recargar el reporte
        if (
            (changes['reportId'] || changes['username']) &&
            !changes['reportId']?.firstChange &&
            !changes['username']?.firstChange
        ) {
            this.loadReport();
        }
    }

    ngOnDestroy(): void {
        this.resetReport();
    }

    /**
     * Carga el reporte de Power BI
     */
    async loadReport(): Promise<void> {
        if (!this.reportId) {
            this.error = 'No se especificó un reporte para mostrar.';
            this.errorCode = null;
            this.isLoading = false;
            return;
        }

        this.isLoading = true;
        this.error = null;
        this.errorCode = null;

        try {
            // Obtener configuración de embed
            await this.fetchEmbedConfig();

            if (this.embedConfig) {
                await this.embedReport();
            }
        } catch (err) {
            // El detalle técnico completo va SOLO a la consola (para devs),
            // nunca a la pantalla del cliente.
            console.error('[PowerBI] Error al cargar el reporte:', err);
            const embedErr = err as EmbedError;
            this.error =
                embedErr?.message ||
                'No pudimos cargar el reporte en este momento. Vuelve a intentarlo o comunícate con soporte.';
            this.errorCode = embedErr?.errorCode ?? null;
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Arma un error de carga con mensaje amigable + código de soporte.
     * Los mensajes técnicos crudos nunca llegan acá: solo el mensaje seguro
     * que arma el backend y su errorCode.
     */
    private buildEmbedError(message?: string, errorCode?: string): EmbedError {
        const e = new Error(
            message ||
                'No pudimos cargar el reporte en este momento. Vuelve a intentarlo o comunícate con soporte.'
        ) as EmbedError;
        e.errorCode = errorCode;
        return e;
    }

    /**
     * Obtiene la configuración de embed desde el backend
     */
    private async fetchEmbedConfig(): Promise<void> {
        return new Promise((resolve, reject) => {
            const request = this.username
                ? this.powerBIApiService.generateEmbedToken({
                      reportId: this.reportId,
                      username: this.username,
                      roles: this.roles,
                  })
                : this.powerBIApiService.getEmbedConfig(this.reportId);

            request.subscribe({
                next: (response) => {
                    if (response.success && response.data) {
                        this.embedConfig = response.data;
                        resolve();
                    } else {
                        reject(
                            this.buildEmbedError(
                                response.message,
                                response.errorCode
                            )
                        );
                    }
                },
                error: (err) => {
                    // err.error es el body del backend: { message, errorCode }
                    reject(
                        this.buildEmbedError(
                            err?.error?.message,
                            err?.error?.errorCode
                        )
                    );
                },
            });
        });
    }

    /**
     * Embebe el reporte en el contenedor
     */
    private async embedReport(): Promise<void> {
        if (!this.embedConfig || !this.reportContainer) {
            return;
        }

        // Resetear reporte anterior si existe
        this.resetReport();

        const config: pbi.IReportEmbedConfiguration = {
            type: 'report',
            tokenType: pbi.models.TokenType.Embed,
            accessToken: this.embedConfig.embedToken,
            embedUrl: this.embedConfig.embedUrl,
            id: this.embedConfig.reportId,
            permissions: pbi.models.Permissions.Read,
            settings: {
                panes: {
                    filters: {
                        visible: true,
                        expanded: false,
                    },
                    pageNavigation: {
                        visible: true,
                    },
                },
                background: pbi.models.BackgroundType.Transparent,
                localeSettings: {
                    language: 'es',
                    formatLocale: 'es-CL',
                },
            },
        };

        try {
            this.report = this.powerbiService.embed(
                this.reportContainer.nativeElement,
                config
            ) as pbi.Report;

            // Manejar eventos del reporte
            this.report.on('loaded', () => {
                console.log('Power BI report loaded successfully');
            });

            this.report.on('error', (event) => {
                const errorDetail = event.detail as { message?: string };
                // El detalle del iframe de Power BI va solo a la consola
                console.error(
                    '[PowerBI] Error dentro del reporte:',
                    errorDetail
                );
                this.error =
                    'Ocurrió un problema al mostrar el reporte. Vuelve a intentarlo.';
                this.errorCode = null;
            });

            this.report.on('rendered', () => {
                console.log('Power BI report rendered successfully');
            });
        } catch (err) {
            console.error('[PowerBI] Error al embeber el reporte:', err);
            throw this.buildEmbedError(
                'No pudimos mostrar el reporte en este momento. Vuelve a intentarlo.'
            );
        }
    }

    /**
     * Resetea el reporte actual
     */
    private resetReport(): void {
        if (this.report) {
            this.powerbiService.reset(this.reportContainer?.nativeElement);
            this.report = null;
        }
    }

    /**
     * Refresca el reporte
     */
    async refreshReport(): Promise<void> {
        if (this.report) {
            try {
                await this.report.refresh();
            } catch (err) {
                console.error('Error refreshing report:', err);
            }
        }
    }

    /**
     * Entra en modo pantalla completa
     */
    async enterFullscreen(): Promise<void> {
        if (this.report) {
            try {
                await this.report.fullscreen();
            } catch (err) {
                console.error('Error entering fullscreen:', err);
            }
        }
    }
}
